import { Injectable } from '@angular/core';

import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';

import * as constants from '../_model/string_literal';

@Injectable({
  providedIn: 'root'
})
export class ClaimService {
  allClaims: any = [];
  selectedClaim: any;
  claimNavigationClaims: any[] = [];
  claimNavigationIndex = -1;

  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      'x-app-source': 'iportal'
    })
  };

  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('token');
    const parsedToken = token ? JSON.parse(token) : null;
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': parsedToken?.token ? `Bearer ${parsedToken.token}` : '',
      'x-app-source': 'iportal'
    });
  }

  get httpOptionsAuth() {
    return { headers: this.getAuthHeaders() };
  }

  constructor(
    private http: HttpClient,
  ) { }

  getAllClaims(limit: any, offset: any) {
    return this.http.get(constants.IAPI_ENDPOINT + `/claim?offset=${offset}&limit=${limit}&recordType=claim`, this.httpOptionsAuth);
  }

  getAllInquiries(limit: any, offset: any) {
    return this.http.get(constants.IAPI_ENDPOINT + `/claim?offset=${offset}&limit=${limit}&recordType=inquiry`, this.httpOptionsAuth);
  }

  getClaimsByPage(offset: number, limit: number, recordType = 'claim') {
    return this.http.get<any[]>(constants.IAPI_ENDPOINT + `/claim?offset=${offset}&limit=${limit}&recordType=${recordType}`, this.httpOptionsAuth);
  }

  getClaimsByAssigneeId(id: any, recordType = 'claim') {
    return this.http.get(constants.IAPI_ENDPOINT + `/claim?assignee=${id}&recordType=${recordType}`, this.httpOptionsAuth);
  }

  getUnassignedClaims(limit: any, offset: any, status: any, recordType = 'claim') {
    return this.http.get(constants.IAPI_ENDPOINT + `/claim?offset=${offset}&limit=${limit}&isAssigned=false&status=${status}&recordType=${recordType}`, this.httpOptionsAuth);
  }

  getAssignedClaims() {
    return this.http.get(constants.IAPI_ENDPOINT + `/claim?isAssigned=true`, this.httpOptionsAuth);
  }

  getClaimStats(recordType = 'claim') {
    return this.http.get(constants.IAPI_ENDPOINT + `/claim/statistics?recordType=${recordType}`, this.httpOptionsAuth);
  }

  getClaimCount(recordType = 'claim') {
    return this.http.get(constants.IAPI_ENDPOINT + `/claim/count?recordType=${recordType}`, this.httpOptionsAuth);
  }

  suggestCsNumber(dateKey: string) {
    return this.http.get<{ dateKey: string; sequence: number; suffix: string }>(
      constants.IAPI_ENDPOINT + `/claim/cs-number/suggest?dateKey=${encodeURIComponent(dateKey)}`,
      this.httpOptionsAuth
    );
  }

  searchClaims(
    title: any,
    readableId: any,
    shipyard: any,
    vesselName: any,
    vesselNo: any,
    fromDate: any,
    toDate: any,
    mark: any,
    tags: any,
    matchMode: any,
    csNumber: any = '',
    recordType = 'claim',
    statuses: string[] = [],
    priorities: number[] = [],
    confidential: boolean | null = null
  ) {
    let params = new HttpParams()
      .set('title', title ?? '')
      .set('readableId', readableId ?? '')
      .set('shipyard', shipyard ?? '')
      .set('vesselName', vesselName ?? '')
      .set('vesselNo', vesselNo ?? '')
      .set('incidentDateFrom', fromDate ?? '')
      .set('incidentDateTo', toDate ?? '')
      .set('mark', mark ?? '')
      .set('tags', Array.isArray(tags) ? tags.join(',') : (tags ?? ''))
      .set('matchMode', matchMode ?? '')
      .set('csNumber', csNumber ?? '')
      .set('recordType', recordType);

    if (statuses.length > 0) {
      params = params.set('status', statuses.join(','));
    }

    if (priorities.length > 0) {
      params = params.set('priority', priorities.join(','));
    }

    if (confidential !== null) {
      params = params.set('confidential', String(confidential));
    }

    return this.http.get(
      constants.IAPI_ENDPOINT + '/claim/search',
      {
        ...this.httpOptionsAuth,
        params,
      }
    );
  }

  getClaimByReadableId(readableId: any, recordType = 'claim') {
    return this.http.get(constants.IAPI_ENDPOINT + `/claim/readable/${readableId}?recordType=${recordType}`, this.httpOptionsAuth);
  }

  setClaimNavigationContext(claims: any[], selectedClaim: any) {
    this.claimNavigationClaims = claims || [];
    this.claimNavigationIndex = this.claimNavigationClaims.findIndex((claim: any) => {
      return claim?.id === selectedClaim?.id || claim?.readableId === selectedClaim?.readableId || claim?.id === selectedClaim?.readableId || claim?.readableId === selectedClaim?.id;
    });
  }

  getClaimForNavigation(direction: 'previous' | 'next') {
    if (this.claimNavigationIndex < 0) {
      return null;
    }

    const nextIndex = direction === 'next' ? this.claimNavigationIndex + 1 : this.claimNavigationIndex - 1;
    if (nextIndex < 0 || nextIndex >= this.claimNavigationClaims.length) {
      return null;
    }

    this.claimNavigationIndex = nextIndex;
    return this.claimNavigationClaims[nextIndex];
  }

  submitNewClaim(newClaimData: any) {
    return this.http.post(constants.IAPI_ENDPOINT + '/claim', JSON.stringify(newClaimData), this.httpOptionsAuth);
  }

  submitNewInquiry(newInquiryData: any) {
    return this.http.post(constants.IAPI_ENDPOINT + '/claim', JSON.stringify(newInquiryData), this.httpOptionsAuth);
  }

  getRecordByGroupId(groupId: any, recordType: any) {
    return this.http.get(constants.IAPI_ENDPOINT + `/claim?inquiryClaimGroupId=${groupId}&recordType=${recordType}`, this.httpOptionsAuth);
  }

  deleteClaim(id: string) {
    return this.http.delete(constants.IAPI_ENDPOINT + '/claim/' + id, {
      ...this.httpOptionsAuth,
      responseType: 'text' as 'text',
    });
  }

  updateClaim(data: any) {
    return this.http.put(constants.IAPI_ENDPOINT + '/claim', JSON.stringify(data), this.httpOptionsAuth);
  }

  convertToClaim(id: string) {
    return this.http.post(constants.IAPI_ENDPOINT + `/claim/${id}/convert-to-claim`, {}, this.httpOptionsAuth);
  }
}
